import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { push } from 'connected-react-router'
import queryString from 'query-string'
import { ApiMiddlewarePayload, ApiResponse } from '@ocean/api'
import { SessionUtils, LoggerUtils } from '@ocean/utils'
import { LoginType } from 'types'
import { AppThunk } from 'store'
import { createApiAction } from 'api'
import { UserUtils } from 'utils'
import { userLoggedOut, logoutUser } from 'core/session/slices/session'
import { redirectToSta, redirectToStaWithBackUrl } from 'core/staIntegration/slices/staIntegration'
import { getUserDeclaration } from 'core/userDeclaration/slices/userDeclaration'
import { getAccount } from 'modules/Auth/api/authService'
import authRoutes from 'modules/Auth/routing/routes'
import { setExternalLoginError, getAccountRequested } from 'modules/Auth/slices/auth'
import { GetAccountResponse, AccountTypes, LoginQueryParams } from 'modules/Auth/types'
import homeRoutes from 'modules/Home/routing/routes'
import { userProxyAuthentication } from 'modules/redirects/ProxyRedirect/api/proxyRedirectService'
import { ProxyRedirectState, ApiProxyAuthenticationResponse } from './types'

const initialState: ProxyRedirectState = {
  authenticationFromProxy: {},
}

const proxyRedirectSlice = createSlice({
  name: 'proxyRedirect',
  initialState,
  reducers: {
    userProxyAuthenticated(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiProxyAuthenticationResponse>>
    ) {
      state.authenticationFromProxy = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const { userProxyAuthenticated } = proxyRedirectSlice.actions

export default proxyRedirectSlice.reducer

const handleProxyUserAuthentification =
  (response: ApiMiddlewarePayload<ApiProxyAuthenticationResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response

    dispatch(setExternalLoginError(undefined))

    if (error) {
      const { customer, userId } = error.details || {}
      UserUtils.storeUserLoginParams({ customer, userId, password: '', loginType: LoginType.Proxy })
      dispatch(setExternalLoginError(error))
      dispatch(push(authRoutes.login))
    }

    if (result) {
      const { customer, userId, sessionToken, isOceanEnabled, shouldUseLegacyClient } =
        result.proxyAuthentication
      SessionUtils.storeSession(sessionToken)
      UserUtils.storeUserLoginParams({ customer, userId, password: '', loginType: LoginType.Proxy })

      const account: ApiResponse<GetAccountResponse> = await getAccount()
      const { success, content } = account

      if (success && content) {
        // Check special user and redirect to STA
        if (content.type === AccountTypes.SpecialUser) {
          LoggerUtils.info('Logged in as SpecialUser')
          dispatch(redirectToSta())
          return
        }

        // Check for legacy params from STA - backUrl means redirect to STA
        const queryParams: LoginQueryParams = queryString.parse(window.location.search)
        const { backUrl } = queryParams
        if (backUrl) {
          dispatch(redirectToStaWithBackUrl(backUrl))
          return
        }

        UserUtils.storeUserAccountInfo(content)
        dispatch(getAccountRequested(content))
        if (!content.isDeclarationConfirmed) {
          LoggerUtils.info("User declaration didn't confirmed")
          dispatch(getUserDeclaration())
        } else {
          // Probably should be conditional depending on if client have OCEAN enabled
          if (!isOceanEnabled) {
            LoggerUtils.info('OCEAN Platform disabled')
            dispatch(redirectToSta())
          } else if (shouldUseLegacyClient) {
            dispatch(redirectToSta({ pluginName: 'TaskStatusPlugin', moduleName: null }))
          } else {
            dispatch(push(homeRoutes.root))
          }
        }
      } else {
        dispatch(logoutUser())
        UserUtils.storeUserLoginParams({
          customer,
          userId,
          password: '',
          loginType: LoginType.Proxy,
        })
        dispatch(setExternalLoginError(account.error))
      }
    }

    dispatch(userProxyAuthenticated(response))
  }

export const authenticateUserFromProxy = (sessionKey: string) => {
  return createApiAction({
    apiCalls: {
      proxyAuthentication: () => userProxyAuthentication(sessionKey),
    },
    payloadAction: userProxyAuthenticated,
    nextAction: handleProxyUserAuthentification,
  })
}
